/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React components

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import SignIn from "layouts/authentication/sign-in";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { isAuthenticated as checkAuthenticated } from "utils/auth";

// Images
import brandLogo from "assets/images/logo.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const isAuthenticated = checkAuthenticated();
  const isAuthPage = pathname.startsWith("/authentication/sign-in");

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              isAuthenticated ? route.component : <Navigate to="/authentication/sign-in" replace />
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && isAuthenticated && !isAuthPage && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brandLogo}
              brandName="Daily Success Friend"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        <Routes>
          <Route
            path="/authentication/sign-in"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignIn />}
          />
          <Route
            path="/authentication/sign-in/basic"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignIn />}
          />
          {getRoutes(routes)}
          <Route
            path="*"
            element={
              <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
            }
          />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && isAuthenticated && !isAuthPage && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brandLogo}
            brandName="Daily Success Friend"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        <Route
          path="/authentication/sign-in"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignIn />}
        />
        <Route
          path="/authentication/sign-in/basic"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignIn />}
        />
        {getRoutes(routes)}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
